/* eslint-disable max-lines */

import { useCallback, useMemo, useRef, useState } from "react"

import {
  Background,
  type Edge,
  Handle,
  type Node,
  type NodeProps,
  type NodeTypes,
  Position,
  ReactFlow,
  ReactFlowProvider,
} from "@xyflow/react"
import { useTranslation } from "react-i18next"

import "@xyflow/react/dist/style.css"

import { Bot, Merge, Play, RefreshCw, Split, UserRound } from "@/components/icons"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Checkbox } from "@/components/ui/checkbox"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Switch } from "@/components/ui/switch"
import { Textarea } from "@/components/ui/textarea"
import { categoryTone } from "@/components/ui/tone-classes"
import { NodeExecutionIndicator } from "@/features/workflow-editor/components/node-execution-indicator"
import { cn } from "@/lib/utils"

type ExecutionState = "idle" | "running" | "completed"

type DemoNodeData = {
  status: ExecutionState
  toolKind?: "web" | "library"
}

const INITIAL_NODE_STATE: Record<string, ExecutionState> = {
  start: "idle",
  parallel: "idle",
  web_search: "idle",
  library_search: "idle",
  merge: "idle",
  llm_summary: "idle",
  end: "idle",
}

const FLOW_LAYOUT: Array<Omit<Node<DemoNodeData>, "data"> & { data?: Partial<DemoNodeData> }> = [
  { id: "start", type: "demoStart", position: { x: 0, y: 160 } },
  { id: "parallel", type: "demoParallel", position: { x: 300, y: 160 } },
  { id: "web_search", type: "demoTool", position: { x: 610, y: 40 }, data: { toolKind: "web" } },
  {
    id: "library_search",
    type: "demoTool",
    position: { x: 610, y: 280 },
    data: { toolKind: "library" },
  },
  { id: "merge", type: "demoMerge", position: { x: 960, y: 160 } },
  { id: "llm_summary", type: "demoLlm", position: { x: 1240, y: 160 } },
  { id: "end", type: "demoEnd", position: { x: 1575, y: 160 } },
]

const FLOW_EDGES: Edge[] = [
  { id: "start-parallel", source: "start", target: "parallel", type: "smoothstep" },
  { id: "parallel-web", source: "parallel", target: "web_search", type: "smoothstep" },
  { id: "parallel-library", source: "parallel", target: "library_search", type: "smoothstep" },
  { id: "web-merge", source: "web_search", target: "merge", type: "smoothstep" },
  { id: "library-merge", source: "library_search", target: "merge", type: "smoothstep" },
  { id: "merge-llm", source: "merge", target: "llm_summary", type: "smoothstep" },
  { id: "llm-end", source: "llm_summary", target: "end", type: "smoothstep" },
].map((edge) => ({
  ...edge,
  style: { stroke: "hsl(var(--muted-foreground))", strokeWidth: 2, opacity: 0.7 },
}))

const DEMO_NODE_TYPES: NodeTypes = {
  demoStart: DemoStartNode,
  demoParallel: DemoParallelNode,
  demoTool: DemoToolNode,
  demoMerge: DemoMergeNode,
  demoLlm: DemoLlmNode,
  demoEnd: DemoEndNode,
}

export function DemoFlowPreview() {
  const { t } = useTranslation("", { keyPrefix: "training.flows.demo" })
  const [nodeStates, setNodeStates] = useState<Record<string, ExecutionState>>(INITIAL_NODE_STATE)
  const [isRunning, setIsRunning] = useState(false)
  const [responseText, setResponseText] = useState("")
  const runIdRef = useRef(0)

  const nodes = useMemo(
    () =>
      FLOW_LAYOUT.map((node) => ({
        ...node,
        data: {
          status: nodeStates[node.id] ?? "idle",
          ...node.data,
        },
      })),
    [nodeStates]
  )

  const handleReset = useCallback(() => {
    runIdRef.current += 1
    setIsRunning(false)
    setNodeStates(INITIAL_NODE_STATE)
    setResponseText("")
  }, [])

  const wait = useCallback(
    (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms)),
    []
  )

  const handleRun = useCallback(async () => {
    if (isRunning) return

    const runId = runIdRef.current + 1
    runIdRef.current = runId
    setIsRunning(true)
    setNodeStates(INITIAL_NODE_STATE)
    setResponseText("")

    const runNode = async (nodeId: string, duration: number) => {
      if (runIdRef.current !== runId) return false
      setNodeStates((prev) => ({ ...prev, [nodeId]: "running" }))
      await wait(duration)
      if (runIdRef.current !== runId) return false
      setNodeStates((prev) => ({ ...prev, [nodeId]: "completed" }))
      return true
    }

    if (!(await runNode("start", 300))) return
    if (!(await runNode("parallel", 200))) return

    const [webOk, libraryOk] = await Promise.all([
      runNode("web_search", 800),
      runNode("library_search", 800),
    ])
    if (!webOk || !libraryOk) return
    if (!(await runNode("merge", 300))) return
    if (!(await runNode("llm_summary", 1000))) return
    if (!(await runNode("end", 200))) return

    const responseMessage = t("chat_response")
    for (let i = 0; i < responseMessage.length; i += 1) {
      if (runIdRef.current !== runId) return
      setResponseText(responseMessage.slice(0, i + 1))
      await wait(12)
    }

    if (runIdRef.current === runId) {
      setIsRunning(false)
    }
  }, [isRunning, t, wait])

  return (
    <Card className="overflow-hidden">
      <CardHeader className="space-y-2">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex flex-wrap items-center gap-2">
            <CardTitle className="text-base">{t("title")}</CardTitle>
            <Badge variant="secondary">{t("badge")}</Badge>
          </div>
          <div className="flex items-center gap-2">
            <Button size="sm" variant="ghost" onClick={handleReset}>
              <RefreshCw className="h-4 w-4" />
              {t("reset")}
            </Button>
            <Button disabled={isRunning} size="sm" onClick={handleRun}>
              <Play className="h-4 w-4" />
              {isRunning ? t("running") : t("run_button")}
            </Button>
          </div>
        </div>
        <p className="text-muted-foreground text-sm">{t("description")}</p>
      </CardHeader>
      <CardContent className="grid gap-4">
        <div className="bg-background/50 min-h-[460px] overflow-hidden rounded-lg border sm:min-h-[540px]">
          <ReactFlowProvider>
            <ReactFlow
              fitView
              panOnDrag
              zoomOnDoubleClick
              zoomOnPinch
              zoomOnScroll
              edges={FLOW_EDGES}
              fitViewOptions={{ padding: 0.1 }}
              maxZoom={1}
              minZoom={0.3}
              nodes={nodes}
              nodesConnectable={false}
              nodesDraggable={false}
              nodesFocusable={false}
              nodeTypes={DEMO_NODE_TYPES}
              panOnScroll={false}
              proOptions={{ hideAttribution: true }}
              selectionOnDrag={false}
            >
              <Background className="opacity-20" gap={24} size={1} />
            </ReactFlow>
          </ReactFlowProvider>
        </div>
        <DemoChatPanel
          chatTitle={t("chat_title")}
          isRunning={isRunning}
          responseText={responseText}
          userInput={t("user_input")}
          waitingText={t("chat_waiting")}
        />
      </CardContent>
    </Card>
  )
}

function DemoChatPanel({
  chatTitle,
  userInput,
  responseText,
  waitingText,
  isRunning,
}: {
  chatTitle: string
  userInput: string
  responseText: string
  waitingText: string
  isRunning: boolean
}) {
  const hasResponse = responseText.length > 0
  const assistantText = hasResponse ? responseText : waitingText

  return (
    <div className="bg-background/60 flex min-h-[320px] flex-col rounded-lg border p-3 sm:min-h-[420px]">
      <div className="text-muted-foreground text-xs font-semibold tracking-wide uppercase">
        {chatTitle}
      </div>
      <div className="mt-3 flex flex-1 flex-col gap-3">
        <ChatBubble icon={<UserRound className="h-4 w-4" />} tone="user">
          {userInput}
        </ChatBubble>
        <ChatBubble
          icon={<Bot className="h-4 w-4" />}
          isMuted={!hasResponse}
          isRunning={isRunning}
          tone="assistant"
        >
          {assistantText}
        </ChatBubble>
      </div>
    </div>
  )
}

function ChatBubble({
  children,
  tone,
  icon,
  isMuted = false,
  isRunning = false,
}: {
  children: string
  tone: "user" | "assistant"
  icon: React.ReactNode
  isMuted?: boolean
  isRunning?: boolean
}) {
  return (
    <div className={cn("flex items-start gap-2", tone === "user" && "justify-end")}>
      {tone === "assistant" && (
        <div className="bg-muted text-muted-foreground flex size-7 items-center justify-center rounded-full">
          {icon}
        </div>
      )}
      <div
        aria-live={tone === "assistant" ? "polite" : undefined}
        className={cn(
          "max-w-[240px] rounded-lg px-3 py-2 text-xs leading-relaxed shadow-sm",
          tone === "assistant" ? "bg-muted text-foreground" : "bg-primary text-primary-foreground",
          isMuted && "text-muted-foreground"
        )}
      >
        <span className="whitespace-pre-line">{children}</span>
        {tone === "assistant" && isRunning && (
          <span className="ml-1 inline-block h-2 w-2 animate-pulse rounded-full bg-current align-middle" />
        )}
      </div>
      {tone === "user" && (
        <div className="border-foreground bg-primary text-primary-foreground flex size-7 items-center justify-center rounded-full border">
          {icon}
        </div>
      )}
    </div>
  )
}

function DemoStartNode({ id, data }: NodeProps) {
  const { t } = useTranslation("", { keyPrefix: "training.flows.demo" })
  const nodeData = data as DemoNodeData
  const isExecuting = nodeData.status === "running"
  const isCompleted = nodeData.status === "completed"

  return (
    <Card
      className={cn(
        "relative min-w-[220px] border-2 shadow-md transition-[border-color,box-shadow] duration-200",
        isExecuting ? "border-success ring-success/30 ring-2" : "border-success",
        isExecuting && "workflow-node-executing",
        isCompleted && !isExecuting && "workflow-node-completed"
      )}
    >
      <CardHeader className={cn("rounded-t-xl p-2", categoryTone(4))}>
        <NodeExecutionIndicator
          hasOutput={false}
          isCompleted={isCompleted}
          isExecuting={isExecuting}
        />
        <CardTitle className="text-center text-sm font-medium">{t("nodes.start")}</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3 p-3">
        <div className="flex items-center justify-between gap-2">
          <Label className="text-xs" htmlFor={`text-input-${id}`}>
            {t("labels.text_input")}
          </Label>
          <Switch checked disabled className="scale-75" id={`text-input-${id}`} />
        </div>
        <div className="space-y-1">
          <Label className="text-xs">{t("labels.files")}</Label>
          <Select value="none" onValueChange={() => undefined}>
            <SelectTrigger disabled className="h-7 text-xs">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="none">{t("labels.no_files")}</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <Handle position={Position.Right} type="source" />
      </CardContent>
    </Card>
  )
}

function DemoParallelNode({ data }: NodeProps) {
  const { t } = useTranslation("", { keyPrefix: "training.flows.demo" })
  const nodeData = data as DemoNodeData
  const isExecuting = nodeData.status === "running"
  const isCompleted = nodeData.status === "completed"

  return (
    <Card
      className={cn(
        "relative min-w-[190px] border-2 shadow-md transition-[border-color,box-shadow] duration-200",
        isExecuting ? "border-success ring-success/30 ring-2" : "border-border",
        isExecuting && "workflow-node-executing",
        isCompleted && !isExecuting && "workflow-node-completed"
      )}
    >
      <CardHeader className={cn("rounded-t-xl p-2", categoryTone(3))}>
        <NodeExecutionIndicator
          hasOutput={false}
          isCompleted={isCompleted}
          isExecuting={isExecuting}
        />
        <CardTitle className="flex items-center justify-center gap-2 text-sm font-medium">
          <Split className="size-4" />
          {t("nodes.parallel")}
        </CardTitle>
      </CardHeader>
      <CardContent className="p-3">
        <div className="text-muted-foreground text-center text-[10px]">
          {t("labels.parallel_description")}
        </div>
        <Handle position={Position.Left} type="target" />
        <Handle position={Position.Right} type="source" />
      </CardContent>
    </Card>
  )
}

function DemoToolNode({ data }: NodeProps) {
  const { t } = useTranslation("", { keyPrefix: "training.flows.demo" })
  const nodeData = data as DemoNodeData
  const isExecuting = nodeData.status === "running"
  const isCompleted = nodeData.status === "completed"
  const toolKind = nodeData.toolKind === "library" ? "library" : "web"
  const toolLabel = toolKind === "library" ? t("nodes.library_search") : t("nodes.web_search")
  const toolId =
    toolKind === "library" ? t("labels.tool_library_search") : t("labels.tool_web_search")
  const searchQueryValue =
    toolKind === "library"
      ? t("labels.library_search_query_value")
      : t("labels.web_search_query_value")

  return (
    <Card
      className={cn(
        "relative min-w-[255px] border-2 shadow-md transition-[border-color,box-shadow] duration-200",
        isExecuting ? "border-success ring-success/30 ring-2" : "border-border",
        isExecuting && "workflow-node-executing",
        isCompleted && !isExecuting && "workflow-node-completed"
      )}
    >
      <CardHeader className={cn("rounded-t-xl p-2", categoryTone(0))}>
        <NodeExecutionIndicator
          hasOutput={false}
          isCompleted={isCompleted}
          isExecuting={isExecuting}
        />
        <CardTitle className="flex items-center justify-between text-sm font-medium">
          <div className="flex flex-col">
            <span>{toolLabel}</span>
            <span className="text-chart-foreground/80 text-[10px] font-normal">{toolId}</span>
          </div>
          <Badge
            className="text-chart-foreground border-chart-foreground text-[10px]"
            variant="outline"
          >
            {t("labels.tool")}
          </Badge>
        </CardTitle>
      </CardHeader>
      <CardContent className="p-3">
        <div className="grid gap-2">
          <div className="grid gap-1.5">
            <Label className="text-xs">{t("labels.search_query")}</Label>
            <Input disabled className="h-7 text-xs" value={searchQueryValue} />
          </div>
          {toolKind === "web" ? (
            <div className="grid gap-1.5">
              <Label className="text-xs">{t("labels.search_mode")}</Label>
              <Select value="all" onValueChange={() => undefined}>
                <SelectTrigger disabled className="h-7 text-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t("labels.search_mode_all")}</SelectItem>
                </SelectContent>
              </Select>
            </div>
          ) : (
            <div className="grid gap-1.5">
              <Label className="text-xs">{t("labels.library_scope")}</Label>
              <Select value="company" onValueChange={() => undefined}>
                <SelectTrigger disabled className="h-7 text-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="company">{t("labels.library_scope_company")}</SelectItem>
                </SelectContent>
              </Select>
            </div>
          )}
          <div className="flex items-center gap-2 border-t pt-2">
            <Checkbox
              checked
              disabled
              className="pointer-events-none"
              id={`show-output-${toolId}`}
            />
            <Label className="text-xs font-normal" htmlFor={`show-output-${toolId}`}>
              {t("labels.show_output")}
            </Label>
          </div>
        </div>
        <Handle position={Position.Left} type="target" />
        <Handle position={Position.Right} type="source" />
      </CardContent>
    </Card>
  )
}

function DemoMergeNode({ data }: NodeProps) {
  const { t } = useTranslation("", { keyPrefix: "training.flows.demo" })
  const nodeData = data as DemoNodeData
  const isExecuting = nodeData.status === "running"
  const isCompleted = nodeData.status === "completed"

  return (
    <Card
      className={cn(
        "relative min-w-[210px] border-2 shadow-md transition-[border-color,box-shadow] duration-200",
        isExecuting ? "border-success ring-success/30 ring-2" : "border-border",
        isExecuting && "workflow-node-executing",
        isCompleted && !isExecuting && "workflow-node-completed"
      )}
    >
      <CardHeader className={cn("rounded-t-xl p-2", categoryTone(3))}>
        <NodeExecutionIndicator
          hasOutput={false}
          isCompleted={isCompleted}
          isExecuting={isExecuting}
        />
        <CardTitle className="flex items-center justify-center gap-2 text-sm font-medium">
          <Merge className="size-4" />
          {t("nodes.merge")}
        </CardTitle>
      </CardHeader>
      <CardContent className="p-3">
        <div className="grid gap-2">
          <div className="grid gap-1.5">
            <Label className="text-xs">{t("labels.merge_strategy")}</Label>
            <Select value="all" onValueChange={() => undefined}>
              <SelectTrigger disabled className="h-7 text-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t("labels.merge_strategy_all")}</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="text-muted-foreground text-center text-[10px]">
            {t("labels.merge_description")}
          </div>
        </div>
        <Handle position={Position.Left} type="target" />
        <Handle position={Position.Right} type="source" />
      </CardContent>
    </Card>
  )
}

function DemoLlmNode({ data }: NodeProps) {
  const { t } = useTranslation("", { keyPrefix: "training.flows.demo" })
  const nodeData = data as DemoNodeData
  const isExecuting = nodeData.status === "running"
  const isCompleted = nodeData.status === "completed"

  return (
    <Card
      className={cn(
        "relative min-w-[275px] border-2 shadow-md transition-[border-color,box-shadow] duration-200",
        isExecuting ? "border-success ring-success/30 ring-2" : "border-border",
        isExecuting && "workflow-node-executing",
        isCompleted && !isExecuting && "workflow-node-completed"
      )}
    >
      <CardHeader className={cn("rounded-t-xl p-2", categoryTone(1))}>
        <NodeExecutionIndicator
          hasOutput={false}
          isCompleted={isCompleted}
          isExecuting={isExecuting}
        />
        <CardTitle className="text-sm font-medium">{t("nodes.llm_summary")}</CardTitle>
      </CardHeader>
      <CardContent className="p-3">
        <div className="grid gap-3">
          <div className="grid gap-1.5">
            <Label className="text-xs">{t("labels.system_prompt")}</Label>
            <Textarea
              disabled
              className="min-h-[80px] text-xs"
              value={t("labels.llm_prompt_value")}
            />
          </div>
          <div className="grid gap-1.5">
            <Label className="text-xs">{t("labels.model")}</Label>
            <Select value="auto" onValueChange={() => undefined}>
              <SelectTrigger disabled className="h-7 text-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="auto">{t("labels.model_auto")}</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-center gap-2 border-t pt-2">
            <Checkbox checked disabled className="pointer-events-none" id="llm-show-output" />
            <Label className="text-xs font-normal" htmlFor="llm-show-output">
              {t("labels.show_output")}
            </Label>
          </div>
        </div>
        <Handle position={Position.Left} type="target" />
        <Handle position={Position.Right} type="source" />
      </CardContent>
    </Card>
  )
}

function DemoEndNode({ data }: NodeProps) {
  const { t } = useTranslation("", { keyPrefix: "training.flows.demo" })
  const nodeData = data as DemoNodeData
  const isExecuting = nodeData.status === "running"
  const isCompleted = nodeData.status === "completed"

  return (
    <Card
      className={cn(
        "relative min-w-[190px] border-2 shadow-md transition-[border-color,box-shadow] duration-200",
        isExecuting ? "border-success ring-success/30 ring-2" : "border-border",
        isExecuting && "workflow-node-executing",
        isCompleted && !isExecuting && "workflow-node-completed"
      )}
    >
      <CardHeader className={cn("rounded-t-xl p-2", categoryTone(3))}>
        <NodeExecutionIndicator
          hasOutput={false}
          isCompleted={isCompleted}
          isExecuting={isExecuting}
        />
        <CardTitle className="text-center text-sm font-medium">{t("nodes.end")}</CardTitle>
      </CardHeader>
      <CardContent className="p-2">
        <div className="text-muted-foreground text-center text-xs">{t("labels.output_label")}</div>
        <Handle position={Position.Left} type="target" />
      </CardContent>
    </Card>
  )
}
